import { useEffect, useRef, useState } from 'react'
import { useIsFetching, useIsMutating, QueryKey } from 'react-query'

interface UseDebouncedLoadingOptions {
  queryKey?: QueryKey
  mutationKey?: QueryKey
  minDuration?: number
}

export const useDebouncedQueryLoadingByKeys = ({
  queryKey,
  mutationKey,
  minDuration = 1000,
}: UseDebouncedLoadingOptions) => {
  const isFetching = useIsFetching({ queryKey })
  const isMutating = useIsMutating({ mutationKey })
  const isLoading = isFetching > 0 || isMutating > 0
  const [showLoader, setShowLoader] = useState(false)
  const loadingStartTimeRef = useRef<number | null>(null)

  useEffect(() => {
    if (isLoading) {
      loadingStartTimeRef.current = Date.now()
      setShowLoader(true)
    } else {
      const start = loadingStartTimeRef.current
      if (start) {
        const elapsed = Date.now() - start
        const remaining = minDuration - elapsed
        if (remaining > 0) {
          const timeout = setTimeout(() => {
            setShowLoader(false)
            loadingStartTimeRef.current = null
          }, remaining)
          return () => clearTimeout(timeout)
        } else {
          setShowLoader(false)
          loadingStartTimeRef.current = null
        }
      } else {
        setShowLoader(false)
      }
    }
    return undefined
  }, [isLoading, minDuration])

  return { isDebouncedLoading: showLoader }
}
